import { useTranslation } from "@impero-com/common/utils/i18n";
import { DonutChart } from "@impero-com/ui";
import { rawPalette } from "@impero-com/ui/styles/theme.css";
import { useNavigate } from "react-router-dom";

import { ActivityBreakdown } from "api/generated/model";
import { useBuildActivitiesUrl } from "org/dashboard/hooks/useBuildActivitiesUrl";

import * as theme from "./ControlHealthActivitiesWidget.css";
import { ControlHealthWidget } from "./ControlHealthWidget";
import { useControlHealthFilters } from "./filters/ControlHealthFiltersProvider";

interface ControlHealthActivitiesWidgetProps {
    breakdown: ActivityBreakdown;
}

export function ControlHealthActivitiesWidget({
    breakdown,
}: ControlHealthActivitiesWidgetProps) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { filters, dateRange } = useControlHealthFilters();
    const getStatusHref = useBuildActivitiesUrl({
        dateRange,
        filteredControlProgramIds: filters.controlPrograms,
        filteredTags: filters.tags,
        filteredEntityIds: filters.entities,
        showArchivedActivities: filters.showArchivedActivities,
        showInactivePrograms: filters.showInactivePrograms,
    });
    const segments = [
        {
            key: "completed",
            label: t("ControlHealth_Activities_Completed"),
            value: breakdown.completed,
            color: rawPalette.activity.completed,
            href: getStatusHref("Completed"),
        },
        {
            key: "completedAfterDue",
            label: t("ControlHealth_Activities_CompletedLate"),
            value: breakdown.completedAfterDue,
            color: rawPalette.activity.completedAfterDue,
            href: getStatusHref("CompletedAfterDueDate"),
        },
        {
            key: "overdue",
            label: t("ControlHealth_Activities_Overdue"),
            value: breakdown.overdue,
            color: rawPalette.activity.overdue,
            href: getStatusHref("Overdue"),
        },
        {
            key: "pending",
            label: t("ControlHealth_Activities_Pending"),
            value: breakdown.pending,
            color: rawPalette.activity.pending,
            href: getStatusHref("Pending"),
        },
        {
            key: "toBeSent",
            label: t("ControlHealth_Activities_ToBeSent"),
            value: breakdown.toBeSent,
            color: rawPalette.activity.toBeSent,
        },
    ];

    return (
        <ControlHealthWidget
            title={t("ControlHealth_Activities_Title")}
            className={theme.card}
        >
            <DonutChart
                segments={segments}
                ariaLabel={t("ControlHealth_Activities_AriaLabel")}
                totalLabel={t("ControlHealth_Activities_Total")}
                emptyLabel={t("ControlHealth_Activities_Empty")}
                onNavigate={navigate}
            />
        </ControlHealthWidget>
    );
}
